// Types from objects received from `server`.
// For now we are keeping the snake casing on the types shared with the server to be easy to lookup
// objects across the client and server. However, we could later potentially introduce
// a client-side API middleware to convert between the casing.

import type { BlockType, WorksheetType } from "../shared/types/worksheet";
import type { CodeKernelLanguage } from "../shared/worksheets/code-kernels";

import { assessmentSchema } from "components/ai/schemas/assessment-schema";
import { SupportedLanguage } from "i18n/i18n";
import type {
  ModelType,
  PageScopedModelType,
  PrincipalOwnedModelType,
  SectionScopedModelType,
  UserScopedModelType,
} from "utils/type-utils";

// Should be in sync with org_user.py
// TODO: These roles are preliminary, we haven't decided on the roles yet, but just distinguish between
// admin and non-admin for now. Update the selectIsOrgAdmin function if this changes.
export type OrgRoleType = "admin" | "recording_agent" | "other" | "none";

export type CoreUserRoleType = "instructor" | "student";

// Should be in sync with section_user.py
export type SectionRoleType = CoreUserRoleType | "recording_agent" | "section_assistant";

// Should be in sync with course_user.py
export type CourseRoleType = "course_admin";

export type SectionPageCategory = "assignment" | "course_resource" | "instructor_workspace";

export const coursePageCategories: SectionPageCategory[] = [
  "assignment",
  "course_resource",
  "instructor_workspace",
];

export type PageCategory = SectionPageCategory | "activity_page";

export enum InteractionMode {
  INDIVIDUAL = "individual",
  SHARED_CLASS = "all",

  // Instructors assign students to groups or students self-assign their groups.
  ASSIGN_STUDENTS = "assign_students",
  SELF_ASSIGN = "self_assign",

  STATIC = "static",
}

export type BulkImportType = "users" | "sections" | "enrollments";

export interface BulkImportJobType extends ModelType {
  title: string;
  import_type: BulkImportType;
  status: "pending" | "started" | "completed" | "error";
  created_at: string;
  run_at: string | null;
  error_message: string | null;
  created_by: string;
  s3_key: string;
  records_processed: number | null;
}

export interface PageType extends SectionScopedModelType {
  category: PageCategory;
  title: string;
  order: number;
  worksheet_id: string; // `collaboration` worksheet CUID
  released_at: string | null;
  last_rolled_up_at: string | null;
  instructor_notes_worksheet_id: string;
  interaction_mode: InteractionMode;
  max_points: number;
  is_imported: boolean;

  // Optional fields based on interaction mode.
  max_group_size: number | null;

  // Optional fields based on type of page.
  activity_id?: string;
  progenitor_id?: string;
  due_at: string | null;
  submissions_last_collected_at: string | null;
  late_submission_interval: string | null;
  assessments_published_at?: string | null;
  assessing_marked_complete_at: string | null;
  class_responses_released_at?: string | null;
  anonymize_student_responses?: boolean;
  should_lock_at_due_date?: boolean;
  has_linked_submissions?: boolean;
  locked_at: string | null;
}

export type ActivityPageType = PageType & {
  category: "activity_page";
  activity_id: string;
};
export type SectionPageType = PageType & {
  category: SectionPageCategory;
  activity_id?: null;
};

export interface PageAccessDataType extends PrincipalOwnedModelType {
  completed_at: string | null;
  completed_by_id?: string | null;
}

export enum ActivityCategory {
  PREP = "prep",
  LESSON = "lesson",
  ADDITIONAL = "additional_resource",
  ARCHIVE = "archive",
}

export type ActivityCategoryType = keyof ActivityCategory;
export type LessonOrArchiveActivityCategory = ActivityCategory.LESSON | ActivityCategory.ARCHIVE;

export interface ActivityType extends SectionScopedModelType {
  order: number;
  topic_id: string;
  title: string;
  estimated_time: number;

  /** A `prep` activity type only contains up to one page. There is
   *  only one `prep` and `additional_resource` activity type per topic/topic.
   *  The `order` field has no effect for a `prep` activity type.
   */
  category: ActivityCategory;
  progenitor_id?: string;
}

// Note: these being computed fields on the backend and not derived client-side from page data,
// means they are not picked up by pubsub when pages are updated, so they might become stale.
export interface TopicCountsType {
  released_page_count?: number;
  unreleased_page_count?: number;
}

// This type does not have any related objects in it, i.e. `activities`.
export interface TopicType extends SectionScopedModelType, TopicCountsType {
  title: string;
  order: number;
  progenitor_id?: string;
  completed_at?: string | null;
}

// This is the format that we do activity import/export in, which is similar to the ActivityType
// but contains fully serialized worksheet data.
export type StoredActivityType = {
  title: string;
  order: number;
  category: ActivityType["category"];
  pages: { title: string; order: number; category: string; worksheetBlocks: BlockType[] }[];
};

export type UserInformationType = {
  email: string;
  first_name: string;
  last_name: string;
};

export type UserType = UserInformationType &
  ModelType & {
    is_superuser: boolean;
    is_anonymized?: boolean;
  };

export interface CourseType extends ModelType {
  code: string;
  grouping_id: string | null;
  title: string;
}

export interface CourseGroupingType extends ModelType {
  title: string;
  parent_id: string | null;
}

export interface CourseGroupingExtendedInsertType extends CourseGroupingType {
  course_ids?: string[];
}

export interface TermType extends ModelType {
  title: string;
  order: number;
  archived_at: string | null;
}

export enum SectionKind {
  LINKED = "linked",
  UNLINKED = "unlinked",
  TEMPLATE = "template",
}

export interface SectionType extends ModelType {
  allow_students_to_join_av: boolean;
  anonymous_grading_enabled: boolean;
  archived_at: string | null;
  auto_recordings_enabled: boolean;
  av_enabled: boolean;
  av_service: string;
  code_blocks_default_language: CodeKernelLanguage;
  code_blocks_enabled: boolean;
  course_id: string;
  kind: SectionKind;
  section_score_activities_percentage: number;
  section_score_assignments_percentage: number;
  section_score_enabled: boolean;
  section_score_prepwork_percentage: number;
  section_score_published_at: string | null;
  section_language: SupportedLanguage | "unspecified";
  section_version: number;
  default_late_submission_interval: string | null;
  description: string;
  external_section_id: string | null;
  hide_static_blocks_on_student_response_page: boolean;
  lms_section_id: string | null;
  mute_students_on_main_room_entry: boolean;
  progenitor_id?: string;
  template_id: string | null;
  projector_zoom: number;
  should_lock_pages_at_due_date: boolean;
  student_ai_assistant_enabled: boolean;
  term_id: string;
  title: string;
}

export type FullSectionType = SectionType & {
  full_title: string;
};

export interface SectionInviteType extends SectionScopedModelType {
  user_id: string;
  invite_key: string;
  created_at: string;
}

export interface SectionUserType extends SectionScopedModelType, UserScopedModelType {
  role: SectionRoleType;
  creation_source: "lms" | "manual";
  is_disabled: boolean;
}

export interface CourseUserType extends UserScopedModelType {
  course_id: string;
  role: CourseRoleType;
}

export interface OrgUserType extends UserScopedModelType {
  org_id: string;
  role: OrgRoleType;
  is_disabled: boolean;
  external_user_id: string | null;
}

export enum ClassSessionCategory {
  IN_PERSON = "in_person",
  VIRTUAL = "virtual",
}

export interface ClassSessionType extends SectionScopedModelType {
  presenting_user_id?: string | null;
  presenting_rollup: boolean;
  presented_page_id?: string | null;
  presented_access_id?: string | null;
  presented_block_id?: string | null;
  presenting_outcomes?: boolean;
  is_hidden_from_session_logs: boolean;
  is_practice: boolean;
  is_projecting: boolean;
  category: ClassSessionCategory | null;
  can_students_screenshare: boolean;
  licode_server_url: string | null;
  screensharing_class_user_id?: string | null;
  breakout_session_ends_at?: string | null;
  breakout_session_synced_page_id?: string | null;
  timer_ends_at?: string | null;
  started_at?: string | null;
  ended_at?: string | null;
  opentok_session_id?: string | null;
  av_active_class_users_ids: string[];
}

export interface ClassSessionSummaryType extends SectionScopedModelType {
  class_session_id: string;
  effective_ended_at: string;
  hand_raise_count: number;
  reaction_count: number;
  chat_message_count: number;
}

export interface ClassSessionUserSummaryType extends SectionScopedModelType {
  class_session_id: string;
  user_id: string;
  role: SectionRoleType;
  presence_minutes: number;
  talk_time_seconds: number;
  first_seen: string | null;
  last_seen: string | null;
}

export type RubricLevelType = {
  name: string;
  description: string;
  score: number;
  conversion_percent: number;
};

export type RubricType = RubricLevelType[];

export enum SectionScoreDisplayFormat {
  Percent = "percent",
  Scale_4 = "scale_4",
  Scale_5 = "scale_5",
}

export interface OrgType extends ModelType {
  current: boolean;
  name: string;
  short_name: string;
  rubric: RubricType;
  sections_can_change_rubric_conversion_percent: boolean;
  section_score_display_format: SectionScoreDisplayFormat;
  lms_type: string;
  max_av_active_participants: number;
}

export type RubricDescriptionType = string;

export interface OutcomeType extends ModelType {
  title: string;
  description?: string;
  outcome_group_id?: string;
  outcome_subgroup_id?: string;
  rubric_descriptions: RubricDescriptionType[] | null;
}

export interface OutcomeSubgroupType extends ModelType {
  outcome_group_id: string;
  title: string;
}

export interface OutcomeGroupType extends ModelType {
  title: string;
}

export interface SectionOutcomeType extends SectionScopedModelType {
  outcome_id: string;
  rubric_descriptions: RubricDescriptionType[] | null;
}

export interface PageOutcomeType extends PageScopedModelType {
  section_outcome_id: string;
  rubric_descriptions: RubricDescriptionType[] | null;
}

export type OutcomeItemType = OutcomeType | SectionOutcomeType | PageOutcomeType;

export interface PageGroupType extends PageScopedModelType {
  created_at_ms: number;
}

export interface PageGroupUserType extends SectionScopedModelType {
  page_group_id: string;
  section_user_id: string;
  is_ready: boolean;
}

export type ReactionType =
  | "like"
  | "dislike"
  | "agree"
  | "disagree"
  | "laugh"
  | "wow"
  | "love"
  | "clap"
  | null;

export type VoteType = "no" | "yes" | null;

export interface ClassUserType extends SectionScopedModelType, UserScopedModelType {
  is_performing_av_check: boolean;
  is_in_class_session: boolean;
  hand_raised_at: string | null;
  av_sort_order: number | null;
  breakout_group_id?: string | null;
  listened_breakout_group_id?: string | null;
  reaction: ReactionType;
  vote: VoteType;
}

export interface BreakoutGroupType extends SectionScopedModelType {
  created_at_ms: number;
  page_group_id?: string | null;
  screensharing_class_user_id?: string | null;
}

export type BreakoutGroupKind = "page" | "random";

export interface ChatMessageType extends SectionScopedModelType {
  author_id: string;
  created_at: string;
  text: string;
}

export type DjangoExtendedModelType<T extends ModelType, Prefix extends string> = {
  [key in keyof T as `${Prefix}__${string & key}`]: T[key];
};

export type ReferentialModelType<T extends ModelType> = T & {
  [K: `${string}__${string}`]: unknown;
};

// Insert model types - These are models that have additional fields that are not
// actually persisted directly in the database, but are used to create the model.
// Note: it's important to keep these in sync with the server-side views.
export type PageExtendedInsertType = {
  initial_worksheet_data: Partial<WorksheetType>;
  instructor_notes_worksheet_data: Partial<WorksheetType>;
  due_now?: boolean;
};
export type TopicExtendedInsertType = {
  instructor_notes_worksheet_data: Partial<WorksheetType>;
};

export type CsrfTokenType = {
  csrftoken: string;
};

export interface ImportSectionRequestType {
  source_section_id: string;
  target_section_id: string;
  include_topics?: boolean;
  include_assignments?: boolean;
  include_course_resources?: boolean;
  include_workspace?: boolean;
}

export interface BaseSubmissionType extends PrincipalOwnedModelType {
  official_at: string | null;
  created_at: string;
  seen_after_assessed_at: string | null;
  worksheet_sharedb_version: number;
  submitted_by_id?: string | null;
}
export interface IndividualSubmissionType extends BaseSubmissionType {
  page_group_id: null;
  section_user_id: string;
}
export interface GroupSubmissionType extends BaseSubmissionType {
  page_group_id: string;
  section_user_id: null;
}
export type SubmissionType = IndividualSubmissionType | GroupSubmissionType;

export interface AssessmentType extends SectionScopedModelType {
  comment?: string;
  points?: number | null;
  submission_id: string;
  assessed_by_id: string;
  created_at: string;
}

// AssessmentType with fields required for creating or updating an assessment
export interface FullAssessmentType {
  page_outcomes?: Record<string, number>;
  is_official?: boolean;
}

export interface OutcomeAssessmentType extends SectionScopedModelType {
  page_outcome_id: string;
  assessment_id: string;
  score: number;
}

export type InviteKeySectionType = {
  title: string;
};

// Keep this in sync with server's raise_formatted_validation_exception
export type FormattedValidationError<TErrorKey = unknown> = {
  [field: string]: {
    details: string | string[];
    t?: TErrorKey | TErrorKey[];
  };
};

/**
 * --------------------------------------------------------------------------------------------
 * AI
 *
 * NOTE: all these definitions must be kept in sync with the corresponding server
 * definitions in monarch/server/ai/types.py.
 * --------------------------------------------------------------------------------------------
 */

/**
 * Language Models
 */

export enum AIModelType {
  // OpenAI models
  GPT_5_NANO = "gpt-5-nano",
  GPT_5_MINI = "gpt-5-mini",
  GPT_5 = "gpt-5",
  GPT_4_1 = "gpt-4.1",
  GPT_4_1_NANO = "gpt-4.1-nano",
  O4_MINI = "o4-mini",
  // Anthropic models
  CLAUDE_3_5_HAIKU = "claude-3-5-haiku-latest",
  CLAUDE_SONNET_4 = "claude-sonnet-4-0",
  CLAUDE_OPUS_4_1 = "claude-opus-4-1",
}

/**
 * Server Messages, a.k.a. Prompts
 */

export type AIServerRoleType = "system" | "assistant" | "user";

export type AIServerMessageType =
  | {
      type: AIServerRoleType;
      role: AIServerRoleType;
      content: string;
      id?: string;
    }
  | {
      type: "function_call";
      name: string;
      call_id?: string;
      arguments: unknown;
    }
  | {
      type: "function_call_output";
      call_id: string;
      output: unknown;
    };

export type AIServerFileType = {
  url: string;
  content_type: string;
  // Name currently only relevant for PDFs
  name?: string;
};

export type AIServerToolType = {
  name: string;
  description: string;
  parameters: Record<string, unknown>;
};

/**
 * Schemas
 */

// AISchemaType is a simple approximation of a concrete type for JSON-schema documents. JSON-schema
// documents may be passed to the AI chat API to specify the expected structure of the chat model's
// response when a structured response is desired. It would be impractical to try to model all the
// specific details that define the vast space of valid JSON-schema documents as a typescript type,
// so this is only a simple, high-level sanity check as well as a means to explicitly label JSON-schema
// document values with that intent in the code. There doesn't seem to be an open-source package that
// defines typescript types to represent JSON-schema documents. The @types/json-schema package comes
// close, but it's out of date and doesn't handle the 2020 version of JSON-schema.
export type AISchemaType = {
  $schema: "https://json-schema.org/draft/2020-12/schema";
  additionalProperties?: boolean;
  description?: string;
  properties: object;
  required?: string[];
  title?: string;
  type: "object";
};

export enum AISchemaId {
  ASSESSMENT = "ASSESSMENT",
}

export const AISchemasById: Record<AISchemaId, AISchemaType> = {
  [AISchemaId.ASSESSMENT]: assessmentSchema,
};

export type AIAssessmentSchemaResponseType = {
  outcomes: Record<string, number>;
  feedback: string;
  refused: string | null;
};

// Map schema types to their corresponding response types
export type AISchemaResponseTypesById = {
  [AISchemaId.ASSESSMENT]: AIAssessmentSchemaResponseType;
};

/**
 * API Request Options
 */

export type AIQueryMode = "chat" | "summarize-responses";

// Basic options that are used by all kinds of AI query requests.
// Server builds system context from section_id/page_id/assessment_mode via ContextBuilder.
type AIBaseRequestOptionsType = {
  section_id: string | null;
  page_id?: string | null;
  /** When set (e.g. assessment), server loads page/section and worksheet version from the submission. */
  submission_id?: string | null;
  model: AIModelType;
  prompt: AIServerMessageType[];
  files?: AIServerFileType[];
};

// Extended options for schema-enabled query requests.
export type AISchemaRequestOptionsType<T extends AISchemaId> = AIBaseRequestOptionsType & {
  schemaId: T;
};

/** Response body for schema-based chat POST (includes optional context for debugger). */
export type AISchemaResponseWrapper<T> = {
  type: "json";
  result: T;
  context?: AIContextPartsResponse;
};

/** One segment of server-reported prompt context for the AI debugger (SSE `context` event). */
export type AIContextPartSystem = {
  kind: "system";
  key: string;
  content: string;
};

export type AIContextPartGitbookMeta = {
  space: string;
  documents: { title: string; source: string; chars: number }[];
  total_chars: number;
};

export type AIContextPartGitbook = {
  kind: "gitbook";
  key: string;
  metadata: AIContextPartGitbookMeta;
};

export type AIContextPartFileMeta = {
  name: string;
  content_type: string;
  bytes: number;
  included: boolean;
};

export type AIContextPartFile = {
  kind: "file";
  key: string;
  metadata: AIContextPartFileMeta;
};

export type AIContextPart = AIContextPartSystem | AIContextPartGitbook | AIContextPartFile;

// Extended options for streaming query requests.
export type AIContextPartsResponse = { parts: AIContextPart[] };

export type AIStreamingRequestOptionsType = AIBaseRequestOptionsType & {
  onTextDelta: (delta: string) => void;
  onToolCalls: (calls: SSEToolCallDataType[]) => void | Promise<void>;
  /** After a server-executed tool finishes; same `call_id` as the matching `function_call` event. */
  onToolCallOutput: (payload: SSEFunctionCallOutputDataType) => void | Promise<void>;
  /** Called with server-built context when returned as first SSE event (for debugger). */
  onContext?: (parts: AIContextPartsResponse["parts"]) => void;
  tools?: AIServerToolType[];
  /** Default `chat`. `summarize-responses` uses compact server context and omits tools. */
  mode: AIQueryMode;
  /** Sent only for `mode: "chat"` — worksheet context for the AI sidebar. */
  share_submissions?: boolean;
  doc_access_id?: string | null;
  assessing?: boolean;
};

// The actual shape of request data that the server can accept.
export type AIServerRequestOptionsType = AIBaseRequestOptionsType & {
  schema?: AISchemaType;
  stream?: boolean;
  tools?: AIServerToolType[];
  /** Chat endpoint only (ignored by query / summarize-responses serializers). */
  share_submissions?: boolean;
  doc_access_id?: string | null;
  assessing?: boolean;
};

/**
 * Streaming responses
 */

// These are the various formats the `data` key of an SSE message can take on, depending
// on the value of the `event` key.
export type SSETextDeltaDataType = { delta: string };
export type SSEToolCallDataType = {
  name: string;
  arguments: Record<string, unknown>;
  call_id: string;
  /** When true, the server runs the tool and emits `function_call_output`; client must not invoke the tool callback. */
  server_execution: boolean;
  /** Present when `server_execution`: localized progress line from ToolService. */
  progress_message?: string;
};

export type SSEFunctionCallOutputDataType = {
  call_id: string;
  output: Record<string, unknown>;
  error?: string;
};

/**
 * --------------------------------------------------------------------------------------------
 * Monitoring
 * --------------------------------------------------------------------------------------------
 */

export interface SectionMonitoringData extends SectionType {
  org: {
    id: string;
    name: string;
    short_name: string;
    av_enabled: boolean;
  };
  instructor?: {
    id: string;
    user_id: string;
    role: string;
    user: UserType;
  };
  student_count: number;
  class_session?: ClassSessionType;
  is_av_class_in_session: boolean;
}

/**
 * --------------------------------------------------------------------------------------------
 * Publishing
 * --------------------------------------------------------------------------------------------
 */

export enum PublicationStatus {
  DRAFT = "draft",
  STARTED = "started",
  COMPLETED = "completed",
  FAILED = "failed",
}

export interface PublicationType extends SectionScopedModelType {
  user_name: string | null;
  status: PublicationStatus;
  user_id: string | null;
  version: number;
  published_at: string | null;
  linked_sections_count: number;
}

/**
 * --------------------------------------------------------------------------------------------
 * Recordings
 * --------------------------------------------------------------------------------------------
 */

export enum RecordingSessionState {
  PREPARING = "preparing",
  STARTING = "starting",
  STARTED = "started",
  STOPPED = "stopped",
}

export interface RecordingSessionType extends SectionScopedModelType {
  class_session_id: string;
  state: RecordingSessionState;
  started_at: string | null;
  stopped_at: string | null;
}
